import { useMemo } from 'react';
import { TractNRI } from 'components/nri-table/types';
import { useExcludeLtds, useShowExpiredPermits } from './hooks';
import { SlotSummaryTable, SlotSummary } from './slot-summary-table';
import {
  SlotSummaryRow,
  SlotVerificationCounts,
  NriVerificationCounts,
} from './types';

interface Props {
  slotSummary: SlotSummaryRow[];
  slots: TractNRI[];
  isLoading: boolean;
  tableId: string;
  showHighlight?: boolean;
}

/**
 * Subtracts excluded counts from total counts when toggle is on.
 * Used for "Exclude LTDs" toggle to subtract LTD verification counts.
 * @param {T | undefined} total - The total verification counts
 * @param {T | undefined} excluded - The excluded verification counts
 * @return {T | undefined} The resulting counts after subtraction
 */
const subtractCounts = <
  T extends SlotVerificationCounts | NriVerificationCounts,
>(
  total: T | undefined,
  excluded: T | undefined
): T | undefined => {
  if (!total) return undefined;
  if (!excluded) return total;

  const result = { ...total };
  for (const key of Object.keys(total) as Array<keyof T>) {
    (result[key] as number) = Math.max(
      0,
      (total[key] as number) - (excluded[key] as number)
    );
  }
  return result;
};

export const SlotsSummary = (props: Props) => {
  const {
    slotSummary,
    slots,
    isLoading,
    tableId,
    showHighlight = false,
  } = props;
  // Two independent toggles (INS-7119)
  const { excludeLtds, setExcludeLtds } = useExcludeLtds(tableId);
  const { showExpiredPermits, setShowExpiredPermits } =
    useShowExpiredPermits(tableId);

  // Count certified slots per well type so we can merge them into the
  // backend-provided verification counts (certified = verified).
  const certifiedByWellType = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const slot of slots) {
      if (slot.wiSource === 'certified') {
        const type = (slot.unitDevelopmentType ?? '').toLowerCase();
        const key =
          type === 'vertical' || type === 'directional'
            ? 'vertical'
            : 'horizontal';
        counts[key] = (counts[key] ?? 0) + 1;
      }
    }
    return counts;
  }, [slots]);

  // Certification-only slot verification counts.
  // Old subtract-level verifications are ignored — only certified slots count as verified.
  // Main counts already exclude expired permit by default (API-level change)
  // Subtract LTD counts only when "Exclude LTDs" toggle is ON
  const enhancedSlotSummary: SlotSummary[] = useMemo(() => {
    return slotSummary.map((row) => {
      const slotCounts = excludeLtds
        ? subtractCounts(row.slotVerification, row.excludedSlotVerification)
        : row.slotVerification;

      const nriCounts = excludeLtds
        ? subtractCounts(row.nriVerification, row.excludedNriVerification)
        : row.nriVerification;

      // Replace backend verification counts with certification-only counts.
      // Clamp certCount to total so that when excludeLtds is ON (total shrinks
      // but certCount still includes LTD-certified slots) we never produce
      // negative notStarted or verified% > 100%.
      const certCount = certifiedByWellType[row.wellType.toLowerCase()] ?? 0;
      const clampedCertCount = Math.min(certCount, slotCounts?.total ?? 0);
      const certOnlySlotCounts = slotCounts
        ? {
            total: slotCounts.total,
            verified: clampedCertCount,
            notStarted: Math.max(0, slotCounts.total - clampedCertCount),
            inProgress: 0,
            incorrect: 0,
          }
        : undefined;

      return {
        ...row,
        slot: certOnlySlotCounts,
        nri: nriCounts,
      };
    });
  }, [slotSummary, excludeLtds, certifiedByWellType]);

  return (
    <SlotSummaryTable
      slotSummary={enhancedSlotSummary}
      isLoading={isLoading}
      displayMode="slots"
      tableId={tableId}
      showHighlight={showHighlight}
      excludeLtds={excludeLtds}
      onExcludeLtdsToggle={setExcludeLtds}
      showExpiredPermits={showExpiredPermits}
      onShowExpiredPermitsToggle={setShowExpiredPermits}
    />
  );
};
